import { Col, Row } from 'antd'
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from 'chart.js'
import React, { useEffect, useRef, useState } from 'react'
import { Bar } from 'react-chartjs-2'
import ArrowIco from '../../../components/icons/ArrowIco'
import { renderTitle } from '../../../utils/helpers'
import { calculateYAxisScale } from './CommonFunction'
import GraphDetails from './GraphDetails'
import {
  ColLarge,
  ColLargeFixed,
  ColLargeScroll,
  ColSmall,
  CustomLegend,
  GraphTitle,
  PlacementContainer,
  StyledDynamicText,
  StyledScrollBarButton,
  StyledSpacer,
} from './StyledComponent'

const cursorPlugin = {
  id: 'cursorPluginStudentPlacement123',
  beforeEvent(chart, args, pluginOptions) {
    if (!chart || !args || !args.event || !args.event.native) return

    const event = args.event
    const rect = event.native.target?.getBoundingClientRect()
    if (!rect) return

    const x = event.native.clientX - rect.left
    const y = event.native.clientY - rect.top

    // Get axes positions
    const yAxis = chart.scales?.y
    const xAxis = chart.scales?.x
    if (!yAxis || !xAxis) return

    const isOverYAxis = x <= yAxis.right && x >= yAxis.left
    const isOverXAxis = y >= xAxis.top && y <= xAxis.bottom

    // Get elements under cursor
    const elements = chart.getElementsAtEventForMode(
      event,
      'nearest',
      { intersect: true },
      false
    )

    if (event.native.target) {
      event.native.target.style.cursor =
        isOverYAxis || isOverXAxis || elements?.length > 0
          ? 'pointer'
          : 'default'
    }
  },
}

// Register the custom plugin
ChartJS.register(
  BarElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  cursorPlugin
)

export default function StudentPlacement({
  data,
  percentageObj,
  studentPlacementArr,
  state,
}) {
  const chartRef = useRef(null)
  const chartRef2 = useRef(null)
  const scrollElement = useRef(null)
  const scrollBar = useRef(null)
  const [isScrollable, setIsScrollable] = useState(false)
  const [scrollWidth, setScrollWidth] = useState(0)
  const [selectedRole, setSelectedRole] = useState(null)
  const totalStudents = Math.max(
    ...(data?.map(item => item?.totalStudents) || [])
  )
  const { stepSize } = calculateYAxisScale(totalStudents)
  const placement = state?.selectedCategory === 'PLACEMENT'

  const chartData = {
    labels: data?.map(item => item?.label),
    // categoryPercentage: 2,
    // barPercentage: 0.8,
    datasets: [
      {
        label: 'Total Students',
        data: data?.map(item => item.totalStudents),
        borderWidth: 1,
        backgroundColor: 'rgba(255,255,255,1)',
        borderColor: '#717171',
        barThickness: 11,
      },
      {
        label: placement ? 'Placed' : 'Selected',
        data: data?.map(item => item.placedStudents),
        borderWidth: 1,
        borderColor: '#0FB680',
        backgroundColor: '#0FB680',
        barThickness: 11,
      },
      {
        label: placement ? 'Un-Placed' : 'Un-Selected',
        data: data?.map(item => item.unplacedStudents),
        borderWidth: 1,
        borderColor: '#F54B56',
        backgroundColor: '#F54B56',
        barThickness: 11,
      },
      {
        label: 'Opt-Out',
        data: data?.map(item => item.optedOutStudents),
        borderWidth: 1,
        borderColor: '#6B7280',
        backgroundColor: '#6B7280',
        barThickness: 11,
      },
    ],
  }

  const options = {
    responsive: true,
    maintainAspectRatio: false, // Allow dynamic height
    categoryPercentage: 0.2, // Adjust spacing between groups (Reduce for more space)
    barPercentage: 0.6,
    layout: {
      padding: {
        top: 10,
        bottom: 20,
        right: 20,
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        grid: {
          drawOnChartArea: false,
          drawTicks: false,
        },
        ticks: {
          color: function (context) {
            return context.tick.value === selectedRole?.value ? 'blue' : 'black'
          },
          cursor: 'pointer',
          autoSkip: false, // Prevents labels from disappearing
          maxRotation: 0, // Rotate labels for better spacing
          minRotation: 0,
          callback: function (value, index) {
            const label = data[index]?.label || ''
            return label.length > 12 ? `${label.slice(0, 12)}...` : label
          },
        },
      },
      y: {
        beginAtZero: true,
        ticks: {
          display: false,
          stepSize: stepSize > 1 ? stepSize : 1,
        },
        grid: {
          drawTicks: false,
          drawBorder: false,
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        displayColors: false,
        // position: 'nearest',
        // events: ['mousemove'],
        // filter: function (tooltipItem) {
        //   const xAxis = tooltipItem.chart.scales.x
        //   const mouseY = tooltipItem.chart.canvas._mousePosition?.y
        //   return mouseY && mouseY >= xAxis.top && mouseY <= xAxis.bottom
        // },
        callbacks: {
          title: function (tooltipItems) {
            const index = tooltipItems[0]?.dataIndex
            return data[index]?.fullLabel || ''
          },
          label: () => null,
          beforeTitle: () => null,
          afterTitle: () => null,
          footer: () => null,
        },
      },
      cursorPlugin,
    },
  }

  const chartData2 = {
    labels: chartData?.labels,
    datasets: chartData?.datasets?.map(item => {
      return {
        label: '',
        data: item?.data,
        borderWidth: 0,
        backgroundColor: 'rgba(255,255,255,1)',
        borderColor: 'transparent',
        barThickness: 0,
      }
    }),
  }

  const options2 = {
    responsive: true,
    maintainAspectRatio: false, // Allow dynamic height
    layout: {
      padding: {
        bottom: 40,
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        grid: {
          drawOnChartArea: false,
          drawTicks: false,
        },
        ticks: {
          display: false,
        },
      },
      y: {
        beginAtZero: true,
        afterFit: function (scaleInstance) {
          scaleInstance.width = 34
        },
        ticks: {
          stepSize: stepSize > 1 ? stepSize : 1,
        },
        grid: {
          drawTicks: false,
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
      },
    },
  }

  const handleChartClick = event => {
    if (!chartRef.current || !event) return

    const chart = chartRef.current
    const {
      canvas,
      scales: { x },
    } = chart

    if (!canvas || !x) return

    const rect = canvas.getBoundingClientRect()
    if (!rect) return

    // Get the mouse click position relative to the canvas
    const xCoOr = event.clientX - rect.left
    const yCoOr = event.clientY - rect.top

    // Get clicked element
    const elements = chart.getElementsAtEventForMode(
      event,
      'nearest',
      { intersect: true },
      false
    )

    if (elements && elements.length > 0) {
      // If clicked on a bar
      const index = elements[0].index
      if (typeof index !== 'undefined' && data[index]) {
        setSelectedRole({
          ...data[index],
          value: index,
          label: data[index]?.fullLabel,
        })
      }
    } else {
      // Check if clicked on x-axis label
      const top = x.top
      const bottom = x.bottom
      const width = x.width / x.ticks.length

      for (let i = 0; i < x.ticks?.length; i++) {
        if (
          xCoOr >= x.left + width * i &&
          xCoOr <= x.left + width * (i + 1) &&
          yCoOr >= top &&
          yCoOr <= bottom &&
          data[i]
        ) {
          setSelectedRole({
            ...data[i],
            value: i,
            label: data[i]?.fullLabel,
          })
          break
        }
      }
    }
  }

  // const handleScrollLeft = () => {
  //   if (scrollBar.current) {
  //     scrollBar.current.scrollBy({ left: -100, behavior: 'smooth' })
  //     scrollElement.current.scrollBy({ left: -100, behavior: 'smooth' })
  //   }
  // }

  // const handleScrollRight = () => {
  //   if (scrollBar.current) {
  //     scrollBar.current.scrollBy({ left: 100, behavior: 'smooth' })
  //     scrollElement.current.scrollBy({ left: 100, behavior: 'smooth' })
  //   }
  // }
  const handleScrollRight = () => {
    if (scrollBar.current) {
      const container = scrollBar.current
      const scrollAmount = container.clientWidth * 0.5 // Scroll by half of the visible width
      const scrollElementAmount = scrollElement?.current?.clientWidth * 0.5
      container.scrollBy({ left: scrollElementAmount, behavior: 'smooth' })
      scrollElement.current.scrollBy({
        left: scrollElementAmount,
        behavior: 'smooth',
      })
    }
  }

  const handleScrollLeft = () => {
    if (scrollBar.current) {
      const container = scrollBar.current
      const scrollAmount = container.clientWidth * 0.5 // Scroll by half of the visible width
      const scrollElementAmount = scrollElement?.current?.clientWidth * 0.5
      container.scrollBy({ left: -scrollElementAmount, behavior: 'smooth' })
      scrollElement.current.scrollBy({
        left: -scrollElementAmount,
        behavior: 'smooth',
      })
    }
  }

  const handleScroll = () => {
    if (scrollElement.current && scrollBar.current) {
      scrollElement.current.scrollLeft = scrollBar.current.scrollLeft
    }
  }
  useEffect(() => {
    if (scrollBar.current) {
      scrollBar.current.addEventListener('scroll', handleScroll)
    }
    return () => {
      if (scrollElement.current) {
        scrollElement.current.removeEventListener('scroll', handleScroll)
      }
    }
  }, [isScrollable])
  useEffect(() => {
    const checkScroll = () => {
      if (scrollElement.current) {
        setIsScrollable(
          scrollElement.current.scrollWidth > scrollElement.current.clientWidth
        )
        setScrollWidth(scrollElement.current.scrollWidth)
      }
    }

    checkScroll()
    window.addEventListener('resize', checkScroll)
    return () => window.removeEventListener('resize', checkScroll)
  }, [data])

  useEffect(() => {
    setSelectedRole(null)
  }, [state?.selectedCategory])

  const handleClearClick = () => {
    setSelectedRole(null)
  }

  return (
    <div>
      <Row gutter={20} style={{ marginTop: 30 }}>
        {studentPlacementArr
          .filter(
            ({ key }) =>
              !(
                state?.selectedCategory !== 'PLACEMENT' &&
                key === 'optedOutStudents'
              )
          )
          .map(({ label, key, color, borderColor }) => (
            <Col
              key={key}
              style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: 20,
              }}
            >
              <CustomLegend
                color={color}
                borderColor={borderColor || 'transparent'}
              />
              <div>
                <b>
                  {key === 'totalStudents'
                    ? percentageObj?.[key] || 'Total'
                    : `${percentageObj?.[key] || '0'}%`}
                </b>
                <StyledDynamicText>{label}</StyledDynamicText>
              </div>
            </Col>
          ))}
      </Row>
      <PlacementContainer>
        <GraphTitle mBottom={35} verticalOrientation>
          Students (in numbers)
        </GraphTitle>
        <ColSmall>
          <Bar ref={chartRef2} data={chartData2} options={options2} />
        </ColSmall>
        <ColLarge
          length={data?.map(item => item?.label)?.length}
          ref={scrollElement}
        >
          <ColLargeFixed
            isScrollable={isScrollable}
            length={data?.map(item => item?.label)?.length}
          >
            <Bar
              ref={chartRef}
              data={chartData}
              options={options}
              onClick={handleChartClick}
            />
          </ColLargeFixed>
        </ColLarge>
      </PlacementContainer>

      <GraphTitle mLeft={45} mBottom={30}>
        {renderTitle({
          domain: state.selectedDomain,
          degree: state.selectedDegree,
          department: state.selectedDepartment,
          specialization: state.selectedSpecialization,
        })}
      </GraphTitle>
      {isScrollable && (
        <div style={{ display: 'flex', position: 'relative' }}>
          <ColSmall></ColSmall>
          <ColLargeScroll
            ref={scrollBar}
            length={data?.map(item => item?.label)?.length}
          >
            <div
              style={{
                width: scrollWidth,
              }}
            ></div>
          </ColLargeScroll>
          <StyledSpacer />
          <>
            <ColSmall></ColSmall>
            <StyledScrollBarButton
              id="scrollUpButton"
              onClick={handleScrollLeft}
              rotate={90}
              width={25}
              marginRight={10}
              position={'absolute'}
              right={0}
              top={-5}
            >
              <ArrowIco active />
            </StyledScrollBarButton>

            <StyledScrollBarButton
              id="scrollDownButton"
              onClick={handleScrollRight}
              rotate={270}
              width={25}
              position={'absolute'}
              right={-15}
              top={-5}
            >
              <ArrowIco />
            </StyledScrollBarButton>
          </>
        </div>
      )}
      <GraphDetails
        data={selectedRole}
        setData={setSelectedRole}
        statusDetail={selectedRole?.data}
        type={'placement'}
        handleClearClick={handleClearClick}
        placement={state?.selectedCategory === 'PLACEMENT'}
      />
    </div>
  )
}
